import React from 'react';
import { Sparkles, ArrowUpRight } from 'lucide-react';
import darkVideo from '../assets/vuzo-dark.mp4';
import lightVideo from '../assets/vuzo-light.mp4';
import { useTheme } from '../context/ThemeContext';

const HeroSection = () => {
  const { isDark } = useTheme();

  return (
    <section className={`relative overflow-hidden ${isDark ? 'bg-black' : 'bg-[#F7FAF8]'}`}>
      {/* Decorative glow blob */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(40,245,171,0.06) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(23,119,85,0.08) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(40,245,171,0.04) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(23,119,85,0.05) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      {/* Video */}
      <div className="relative z-10 w-full pt-10 overflow-hidden px-4 md:px-16">
        <div className="relative w-full flex items-center justify-center">
          <div className="animate-float w-full max-w-[900px] mx-auto">
          <video
            key={isDark ? 'dark' : 'light'}
            autoPlay muted loop playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            className="w-full rounded-xl"
            style={{ pointerEvents: 'none' }}
          >
            <source src={isDark ? darkVideo : lightVideo} type="video/mp4" />
          </video>
          </div>
        </div>
        <div className={`absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t ${isDark ? 'from-black' : 'from-[#F7FAF8]'} to-transparent`} />
      </div>

      {/* Text content */}
      <div className="relative z-10 w-full py-16 lg:py-24 px-4 md:px-0">
        <div className="max-w-[1280px] mx-auto px-0 md:px-14 2xl:px-0">
          <div className="flex flex-col items-center lg:items-start space-y-5 text-center lg:text-left">

            {/* Badge */}
            <span className={`inline-flex items-center rounded-[10px] text-sm font-medium px-4 py-2 ${
              isDark ? 'bg-[#17775510]' : 'bg-[#177755]/8 border border-[#177755]/20'
            }`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${isDark ? 'bg-[#104B30]' : 'bg-[#177755]/10'}`}>
                <Sparkles className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-[#177755]'}`} />
              </div>
              <span className={`font-medium ${isDark ? 'text-[#28F5AB]' : 'text-[#177755]'}`}>
                All-in-One AI Personalized Assistant
              </span>
            </span>

            {/* Title */}
            <h1 className="text-[44px] md:text-[58px] font-bold leading-none md:leading-tight">
              <span className={isDark ? 'text-white' : 'text-[#0F172A]'}>
                <span className={isDark ? 'text-[#28F5AB]' : 'text-[#177755]'}>Vuzo:</span> AI Productivity
              </span>
              <br />
              <span className={isDark ? 'text-white' : 'text-[#0F172A]'}>Right in Your Browser</span>
            </h1>

            {/* Description */}
            <p className={`text-[16px] md:text-[18px] leading-relaxed lg:max-w-2xl ${isDark ? 'text-gray-300' : 'text-[#475569]'}`}>
              Improving conversations and boosting productivity is easier than ever.
              With our Premium Package, you'll get unlimited access to powerful AI tools like
              GPT-5.4 Pro, Gemini 2.5 Flash, Claude Sonnet, and more — built to streamline your workflow,
              spark creativity, and help you accomplish more with less effort.
            </p>

            {/* CTA */}
            <a
              href="https://chat.vuzo.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-[16px] font-semibold transition-all duration-200 shadow-lg ${
                isDark
                  ? 'bg-[#28F5AB] text-[#0A0A0A] hover:bg-[#1de098] shadow-[0_4px_24px_rgba(40,245,171,0.25)]'
                  : 'bg-[#177755] text-white hover:bg-[#0f5b3e] shadow-[0_4px_24px_rgba(23,119,85,0.25)]'
              }`}
            >
              Start for Free
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
